import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../services/firebase";
import { ImageOff, ZoomIn } from "lucide-react";
import ImageLightbox from "./ImageLightbox";

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

/** Resolve a Firebase Storage path to a download URL; plain URLs pass through. */
const resolveSrc = async (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isHttpUrl(raw)) return raw;
  try {
    return await getDownloadURL(ref(storage, raw));
  } catch (err) {
    console.warn("[ProductImage] could not resolve image", raw, err);
    return "";
  }
};

const collectImages = (images, url) => {
  const list = Array.isArray(images)
    ? images.map((i) => (typeof i === "string" ? i : i?.url))
    : [];
  if (!list.length && url) list.push(url);
  return [...new Set(list.map((v) => String(v || "").trim()).filter(Boolean))];
};

const placeholder = (size) => (
  <div
    title="No image"
    style={{
      height: size,
      width: size,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px dashed #e2e8f0",
      borderRadius: 6,
      color: "#94a3b8",
      background: "#fafafa",
    }}
  >
    <ImageOff size={Math.round(size * 0.45)} />
  </div>
);

/**
 * Product image thumbnail. Clicking it opens a full-screen viewer with
 * forward / back navigation over every image on the order.
 *
 * Accepts either `images` (the order's image array) or a single `url`.
 */
const ProductImage = ({
  images,
  url,
  productName = "Product",
  size = 36,
  label,
}) => {
  const keys = useMemo(() => collectImages(images, url), [images, url]);
  const [resolved, setResolved] = useState([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const out = await Promise.all(keys.map(resolveSrc));
      if (alive) setResolved(out);
    })();
    return () => {
      alive = false;
    };
  }, [keys]);

  const close = useCallback(() => setOpen(false), []);

  const total = resolved.length;
  if (!total) return placeholder(size);
  const title = label || productName;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`View ${title} image`}
        onClick={() => {
          setIndex(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIndex(0);
            setOpen(true);
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={total > 1 ? `View ${total} images` : "View image"}
        style={{
          position: "relative",
          height: size,
          width: size,
          cursor: "zoom-in",
          borderRadius: 6,
          border: hover ? "1px solid #f59e0b" : "1px solid #e2e8f0",
          backgroundColor: "#fafafa",
          boxShadow: hover ? "0 2px 8px rgba(245,158,11,0.25)" : "none",
          transition: "box-shadow 0.15s, border-color 0.15s",
        }}
      >
        <img
          src={resolved[0]}
          alt={title}
          style={{
            height: "100%",
            width: "100%",
            objectFit: "contain",
            borderRadius: 5,
            display: "block",
          }}
        />
        {total > 1 && (
          <span
            style={{
              position: "absolute",
              right: -5,
              bottom: -5,
              backgroundColor: "#0f172a",
              color: "#fff",
              borderRadius: 999,
              padding: "0 5px",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "14px",
            }}
          >
            {total}
          </span>
        )}
        {hover && (
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0f172a",
              backgroundColor: "rgba(255,255,255,0.72)",
              borderRadius: 5,
            }}
          >
            <ZoomIn size={Math.round(size * 0.5)} />
          </span>
        )}
      </div>

      {open && (
        <ImageLightbox
          images={resolved}
          index={index}
          onIndexChange={setIndex}
          onClose={close}
          title={title}
        />
      )}
    </>
  );
};

export default ProductImage;